<template>
  <section class="order-payment-details" aria-label="Payment details">
    <h3
      class="text-subtitle-1 font-weight-bold"
      :class="compact ? 'mb-1' : 'mb-3'"
    >
      Payments
    </h3>
    <p v-if="!shown.length" class="text-medium-emphasis">No payments yet.</p>
    <div v-if="compact && shown.length" class="payment-lines">
      <div
        v-for="payment in shown"
        :key="paymentId(payment)"
        class="payment-line"
        :class="{ 'payment-cancelled': paymentStatus(payment) === 'CANCELLED' }"
      >
        <span
          class="payment-line-title font-weight-bold"
          :title="paymentDescription(payment)"
        >
          {{ paymentType(payment) }} #{{ paymentId(payment) }}
          <v-chip
            v-if="statusChip(payment)"
            :color="statusChip(payment)!.color"
            size="x-small"
            class="ml-1"
            data-testid="payment-status"
            >{{ statusChip(payment)!.label }}</v-chip
          >
        </span>
        <span class="text-no-wrap" :title="paymentDescription(payment)">{{
          money(paymentAmount(payment))
        }}</span>
        <slot
          name="actions"
          :payment="payment"
          :attempt="paymentAttempt(payment)"
        />
      </div>
    </div>
    <template v-else>
      <v-card
        v-for="payment in shown"
        :key="paymentId(payment)"
        variant="outlined"
        class="mb-3"
      >
        <v-card-text
          :class="{
            'payment-cancelled': paymentStatus(payment) === 'CANCELLED',
          }"
        >
          <div class="d-flex justify-space-between align-center ga-3">
            <div class="d-flex align-center flex-wrap ga-2">
              <strong
                >{{ paymentType(payment) }} #{{ paymentId(payment) }}</strong
              >
              <v-chip
                v-if="statusChip(payment)"
                :color="statusChip(payment)!.color"
                size="small"
                data-testid="payment-status"
                >{{ statusChip(payment)!.label }}</v-chip
              >
            </div>
            <strong class="text-no-wrap">{{
              money(paymentAmount(payment))
            }}</strong>
          </div>
          <div
            v-for="detail in paymentFacts(payment)"
            :key="detail.label"
            class="payment-detail-row mt-2"
          >
            <span>{{ detail.label }}</span
            ><span>{{ money(detail.amount) }}</span>
          </div>
          <div v-if="$slots.actions" class="mt-3">
            <slot
              name="actions"
              :payment="payment"
              :attempt="paymentAttempt(payment)"
            />
          </div>
        </v-card-text>
      </v-card>
    </template>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  memberPaymentStatusChip,
  memberPaymentVisible,
  staffPaymentStatusChip,
} from "@/composables/shop/paymentStatus";
import type { OrderPaymentStaff } from "../../../cbi-generator/generated/dto/OrderPaymentStaff";
import type { PaymentAttempt } from "../../../cbi-generator/generated/dto/PaymentAttempt";

type Payment = OrderPaymentStaff | PaymentAttempt;
const props = defineProps<{
  payments: readonly Payment[];
  attempts?: readonly PaymentAttempt[];
  compact?: boolean;
  /** Member view: hides unfinished payments and shows only dispute/refund chips. */
  member?: boolean;
}>();
const shown = computed(() =>
  props.member
    ? props.payments.filter((payment) =>
        memberPaymentVisible(paymentStatus(payment)),
      )
    : props.payments,
);
const statusChip = (payment: Payment) =>
  props.member
    ? memberPaymentStatusChip(paymentStatus(payment))
    : staffPaymentStatusChip(paymentStatus(payment));
const paymentId = (payment: Payment) =>
  "assignId" in payment ? payment.assignId : payment.payment.assignId;
const paymentAttempt = (payment: Payment) =>
  "assignId" in payment
    ? props.attempts?.find(
        (attempt) => attempt.payment.assignId === payment.assignId,
      )
    : payment;
const paymentStatus = (payment: Payment) =>
  "assignId" in payment ? payment.paymentStatus : payment.status;
const paymentAmount = (payment: Payment) =>
  "assignId" in payment ? payment.paymentTotalCents : payment.amountCents;
const paymentType = (payment: Payment) =>
  "assignId" in payment
    ? (payment.paymentType ?? "Payment").replaceAll("_", " ")
    : "Payment";
const paymentFacts = (payment: Payment) => {
  if (!("assignId" in payment)) return [];
  if (props.member)
    return [{ label: "Refunded", amount: payment.paymentRefundedCents }].filter(
      (detail) => detail.amount,
    );
  const facts = [
    {
      label: "Authorized, awaiting capture",
      amount: payment.paymentCapturableCents,
    },
    { label: "Settled", amount: payment.paymentPaidCents },
    { label: "Refunded", amount: payment.paymentRefundedCents },
  ].filter((detail) => detail.amount);
  if (payment.paymentType === "CASH" && payment.paymentStatus === "SUCCESSFUL")
    facts.push({
      label: "Cash change due",
      amount: payment.paymentServiceChangeCents,
    });
  return facts;
};
const paymentDescription = (payment: Payment) =>
  [
    statusChip(payment)?.label,
    ...paymentFacts(payment).map(
      (fact) => `${fact.label}: ${money(fact.amount)}`,
    ),
  ]
    .filter(Boolean)
    .join("; ");
const money = (value: number | null) =>
  value == null
    ? "--"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value / 100);
</script>

<style scoped>
.payment-cancelled {
  text-decoration: line-through;
}
.payment-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
}
.payment-line + .payment-line {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.payment-line-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.payment-detail-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.875rem;
}
</style>
